import { datadogLogs } from "@datadog/browser-logs";
import { ensureClientCorrelationId } from "../correlation";
import { createConsoleLogger, Logger } from "cy-logging";
import { DatadogFormatter } from "../formatter";

// Log level hierarchy
const LOG_LEVELS = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
} as const;

type LogLevel = keyof typeof LOG_LEVELS;

/**
 * Creates a client-side Datadog logger for structured logging.
 *
 * This logger provides structured client-side logging that:
 * - Maintains correlation IDs for request tracing
 * - Uses consistent formatting with server-side logs
 * - Falls back to console logging when Datadog is unavailable or not yet initialized
 *
 * @param name - Logger name/component name
 * @param config - Datadog configuration
 * @returns Logger instance with client-side Datadog integration or console fallback
 */
export function createClientDatadogLogger(name: string): Logger {
  if (typeof window === "undefined") {
    console.debug(
      "returning console logger for client because window is undefined",
    );
    return createConsoleLogger(name);
  }

  const formatter = new DatadogFormatter("client");

  const _context: Record<string, unknown> = {};

  const buildContext = (
    correlationId: string,
    level: LogLevel,
    context: unknown,
  ) => {
    const baseContext = {
      ..._context,
      correlationId,
      component: name,
      source: "client",
    };

    if (context == null) return baseContext;

    if (level === "error" && context instanceof Error) {
      const error = context;
      return {
        ...baseContext,
        error,
      };
    }

    if (typeof context === "object" && !(context instanceof Error)) {
      return { ...baseContext, ...context };
    }

    return { ...baseContext, context };
  };

  const cachedCorrelationId = ensureClientCorrelationId();

  const logMessage = (
    level: LogLevel,
    message: string,
    context?: Record<string, unknown>,
    error?: unknown,
  ) => {
    const contextWithMetadata = buildContext(
      cachedCorrelationId,
      level,
      context,
    );
    const formattedEntry = formatter.format(
      level,
      message,
      contextWithMetadata,
    );

    const errorToLog =
      error instanceof Error
        ? error
        : level === "error" && context instanceof Error
          ? context
          : undefined;

    try {
      datadogLogs.logger[level](message, formattedEntry.context, errorToLog);
    } catch (datadogError) {
      console[level](
        ` ${level.toUpperCase()}: ${message}`,
        formattedEntry.context,
        errorToLog,
      );
      if (process.env.NODE_ENV === "development") {
        console.warn(
          ` Datadog logging failed, using console fallback:`,
          datadogError,
        );
      }
    }
  };

  return {
    debug: (
      message: string,
      context?: Record<string, unknown>,
      error?: unknown,
    ) => logMessage("debug", message, context, error),
    info: (
      message: string,
      context?: Record<string, unknown>,
      error?: unknown,
    ) => logMessage("info", message, context, error),
    warn: (
      message: string,
      context?: Record<string, unknown>,
      error?: unknown,
    ) => logMessage("warn", message, context, error),
    error: (
      message: string,
      context?: Record<string, unknown>,
      error?: unknown,
    ) => logMessage("error", message, context, error),
    get context() {
      return { ..._context };
    },
    addContext(entries: Record<string, unknown>) {
      Object.assign(_context, entries);
    },
    child: (childName: string) => {
      const child = createClientDatadogLogger(`${name}.${childName}`);
      child.addContext(_context);
      return child;
    },
  };
}
